import React, { useMemo, useState } from 'react';
import { AlertTriangle, LineChart, ChartLine, DollarSign, ArrowUpDown, CalendarDays, Camera, Layers, Milestone } from 'lucide-react';
import { SkeletonStatCards, SkeletonTable } from '../../../components/common/SkeletonLoader';
import { formatCents } from '../../../hooks/pipelineTracker/usePipelineReport';
import { useTrendsReport, TrendPoint } from '../../../hooks/pipelineTracker/useTrendsReport';
import { Panel, Pill, Honesty, money, num } from './pipelineParts';
import { CHART_PALETTE, Chip, HousePanel, HouseStatCard, HouseStatGrid, PeriodBar, ToneBanner, chartColour } from './trackerKit';
import {
  DEFAULT_RANGE,
  RANGE_PRESETS,
  change,
  readRange,
  resolveRange,
  stageShare,
  type PreviousRange,
  type RangeKey,
  type RangeReading,
} from './trendsRange';
import StageTrendChart from './StageTrendChart';
import StageMovementPanel from './StageMovementPanel';
import MonthlyBreakdownChart from '../../../components/charts/MonthlyBreakdownChart';
import { TrackerSection } from './TrackerSection';

/**
 * Headline totals, one point per published snapshot.
 *
 * The figures are IN-FLIGHT deals on the Pipeline tab's definition, produced by
 * the SAME aggregator, so the two tabs cannot disagree.
 *
 * NOTHING IS MODELLED OR BACK-FILLED. A month before the tenant's first upload
 * cannot be reconstructed from a snapshot, because a snapshot carries a deal's
 * current stage and not its history. Those months stay visibly empty rather
 * than being filled with an estimate, which is the whole reason the first
 * number here is one a practice can act on.
 */

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const formatAsAt = (iso: string): string => {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
};

/** Compact axis label: '5 Sep'. The full date lives in the tooltip. */
const shortAsAt = (iso: string): string => {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.getUTCDate()} ${MONTH_LABELS[d.getUTCMonth()]}`;
};

const addWeeks = (iso: string, weeks: number): string => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + weeks * 7);
  return d.toISOString().slice(0, 10);
};

/**
 * The milestone timeline.
 *
 * Dates are computed from the tenant's OWN first upload, not from a fixed
 * calendar, so a practice that starts in November sees November dates. Each
 * milestone states what unlocks and when, so an empty tab reads as a schedule
 * rather than as a missing feature.
 */
function milestones(firstAsAt: string, snapshotCount: number) {
  return [
    { when: formatAsAt(firstAsAt), what: 'history starts', reached: true },
    { when: `Week 4 · ${formatAsAt(addWeeks(firstAsAt, 4))}`, what: 'first conversion read', reached: snapshotCount >= 4 },
    { when: `Week 8 · ${formatAsAt(addWeeks(firstAsAt, 8))}`, what: 'stage velocity', reached: snapshotCount >= 8 },
    { when: `Month 3 · ${formatAsAt(addWeeks(firstAsAt, 13))}`, what: 'full trend set', reached: snapshotCount >= 13 },
  ];
}

/**
 * One row per snapshot, oldest first: the book as each export saw it.
 *
 * WHY EVERY EXPORT, NOT EVERY MONTH. This chart kept only the last export of each month, so six
 * weekly uploads from 3 Sep drew two points, 28 Sep and 6 Oct, and read as "nothing before late
 * September" (founder, 6 Oct 2026). Exports are weekly, so the week is the grain the data has.
 *
 * Only real snapshots appear: nothing is drawn for a week with no upload, before the first or
 * after the last. Labels are '3 Sep', with the year added ('3 Sep 25') once the range spans more
 * than one calendar year, so two Septembers never share a label.
 */
export function snapshotRows(points: TrendPoint[]): Array<{ month: string; asAtDate: string; deals: number; value: number }> {
  const sorted = [...points].sort((x, y) => (x.asAtDate < y.asAtDate ? -1 : x.asAtDate > y.asAtDate ? 1 : 0));
  const years = new Set(sorted.map((p) => p.asAtDate.slice(0, 4)));
  return sorted.map((p) => ({
    month: years.size > 1 ? `${shortAsAt(p.asAtDate)} ${p.asAtDate.slice(2, 4)}` : shortAsAt(p.asAtDate),
    asAtDate: p.asAtDate,
    deals: p.deals,
    // Dollars: MonthlyBreakdownChart formats currency itself, and cents would read a hundredfold.
    value: Math.round(p.loanAmountCents / 100),
  }));
}

/** Above this many points the value printed on every dot crowds the line; the tooltip keeps them. */
const LABELLED_POINTS_MAX = 14;

/**
 * Enough weekly points for a per-stage movement chart to say anything.
 *
 * Four is the tenant's own first milestone, not a round number. Below it a
 * stage's "trend" is two or three dots: a line through them reads as a
 * direction the data does not support, which is exactly the estimate this tab
 * refuses to make everywhere else. Under four points the milestone schedule is
 * the honest content, and it is what a tenant on their second upload sees.
 */
const MOVEMENT_MIN_POINTS = 4;

/**
 * One colour per stage from the house chart palette (trackerKit CHART_PALETTE), assigned in the
 * server's canonical funnel order (the key order of `stages`), never by sorting the labels, so a
 * stage keeps its colour whichever range is chosen.
 */
type StageSeries = {
  label: string;
  colour: string;
  shares: number[];
  now: number;
  was: number;
  deltaPp: number;
  /** The same stage's move over the previous range of equal length, or null when there is none. */
  prevDeltaPp: number | null;
};

/** Share-of-book series per canonical stage, in funnel order, over the range's points. */
function buildStageSeries(points: TrendPoint[], previous: PreviousRange): StageSeries[] {
  const labels = Object.keys(points[points.length - 1].stages);
  return labels.map((label, i) => {
    const shares = points.map((p) => stageShare(p, label));
    return {
      label,
      colour: chartColour(i),
      shares,
      now: points[points.length - 1].stages[label] ?? 0,
      was: points[0].stages[label] ?? 0,
      deltaPp: shares[shares.length - 1] - shares[0],
      prevDeltaPp: previous.status === 'ok' ? shares[0] - stageShare(previous.prevStart, label) : null,
    };
  });
}

/** Up, down, or neither. A book that did not move must not read as a fall. */
const arrow = (v: number): string => (v > 0.05 ? '\u2191' : v < -0.05 ? '\u2193' : '\u2192');

const pp = (v: number): string => `${v > 0 ? '+' : ''}${v.toFixed(1)} pp`;
const deltaClass = (v: number): string =>
  (v > 0.05 ? 'text-emerald-400' : v < -0.05 ? 'text-red-400' : 'text-gray-500');

/**
 * Replaces a placeholder that never filled.
 *
 * The panel it supersedes rendered the same centred paragraph at every snapshot
 * count: the milestone chips lit up as uploads accumulated, but no branch ever
 * showed conversion or velocity content, so a tenant at thirteen snapshots read
 * "full trend set" beside an empty box.
 *
 * Two chart forms were tried and abandoned before this one. A stacked area put
 * thirteen bands on a moving baseline, so only the bottom band could be read. A
 * single shared 0-45% axis across small multiples fixed that but flattened every
 * stage under 7% into a straight line at the foot of its panel: comparable, and
 * unreadable.
 *
 * So each panel is scaled to its own range and size is carried separately, by the
 * share-of-book bar beneath it. The chart answers "what shape is this stage in",
 * the bar answers "how much of the book is it", and neither has to do both.
 *
 * Drawing is delegated to StageTrendChart, which is recharts, so stroke weights
 * and tooltips match the Dashboard instead of depending on viewBox scaling.
 */
const StageMovement: React.FC<{ reading: RangeReading }> = ({ reading }) => {
  const { points, previous, range } = reading;
  const series = useMemo(() => buildStageSeries(points, previous), [points, previous]);
  const ranked = useMemo(
    () => [...series].sort((a, b) => Math.abs(b.deltaPp) - Math.abs(a.deltaPp)),
    [series],
  );
  const lead = ranked[0];
  const first = formatAsAt(points[0].asAtDate);
  const last = formatAsAt(points[points.length - 1].asAtDate);
  const maxShare = Math.max(1, ...series.map((s) => s.shares[s.shares.length - 1]));

  /** Recharts rows for one stage: label, its share, and the deals behind it. */
  const rowsFor = (s: StageSeries) =>
    points.map((p, i) => ({
      month: shortAsAt(p.asAtDate),
      share: s.shares[i],
      deals: p.stages[s.label] ?? 0,
    }));

  return (
    <Panel
      title="Stages that moved"
      icon={Layers}
      accent={CHART_PALETTE[3]}
      badge={<>
        <Chip tone="violet">{`${num(points.length)} weekly snapshots`}</Chip>
        {previous.status !== 'ok' && <Chip>{previous.reason}</Chip>}
      </>}
      blurb={`Each stage's share of deals in flight. Every panel is scaled to its OWN range, printed on the axis, so a stage holding 4% of the book shows its shape instead of flattening against one holding 41%. The bar under each chart carries share of book, which is where size is compared. Hover any week for its figures. Ordered by how far each stage moved between ${first} and ${last}${previous.status === 'ok' ? `; each card also shows its move over the previous ${range.label}` : ''}.`}
    >
      {lead && Math.abs(lead.deltaPp) >= 1 && (
        <p className="mb-4 text-sm leading-relaxed text-gray-300">
          <span className="font-semibold text-gray-100">{lead.label}</span>
          {lead.deltaPp > 0 ? ' has grown from ' : ' has fallen from '}
          <span className="tabular-nums">{lead.shares[0].toFixed(1)}%</span>
          {' of deals in flight to '}
          <span className="tabular-nums">{lead.shares[lead.shares.length - 1].toFixed(1)}%</span>
          {', '}<span className="tabular-nums">{num(lead.was)}</span>{' deals to '}
          <span className="tabular-nums">{num(lead.now)}</span>{'.'}
        </p>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {ranked.map((s) => (
          <div
            key={s.label}
            data-testid="stage-card"
            className="relative overflow-hidden rounded-lg border border-gray-700/80 bg-gray-900/50 p-4 transition-colors hover:border-gray-600"
          >
            <div aria-hidden="true" className="absolute inset-x-0 top-0 h-[0.125rem]" style={{ background: s.colour }} />
            <div className="flex items-baseline gap-2">
              <span className="h-2 w-2 flex-none rounded-sm" style={{ background: s.colour }} />
              <span className="min-w-0 flex-1 truncate text-[0.8125rem] font-semibold text-gray-200">{s.label}</span>
              <span className={`text-xs font-bold tabular-nums ${deltaClass(s.deltaPp)}`} title={`Since ${first}`}>{pp(s.deltaPp)}</span>
            </div>
            {s.prevDeltaPp !== null && (
              <div className="mt-0.5 text-right text-[0.6875rem] text-gray-500" data-testid="stage-prev">
                {`Previous ${range.label} `}
                <span className={`font-semibold tabular-nums ${deltaClass(s.prevDeltaPp)}`}>{pp(s.prevDeltaPp)}</span>
              </div>
            )}
            <div className="mt-1.5 flex items-baseline gap-2">
              <span className="text-2xl font-bold leading-none tabular-nums text-gray-100">
                {s.shares[s.shares.length - 1].toFixed(1)}%
              </span>
              <span className="text-xs tabular-nums text-gray-500">
                {num(s.now)} deals, was {num(s.was)}
              </span>
            </div>
            <div className="mt-2">
              <StageTrendChart label={s.label} colour={s.colour} data={rowsFor(s)} />
            </div>
            <div className="mt-2 flex items-center gap-2">
              <span className="h-1 flex-1 overflow-hidden rounded-sm bg-gray-700/50">
                <span
                  className="block h-1 rounded-sm"
                  style={{ background: s.colour, width: `${Math.round((s.shares[s.shares.length - 1] / maxShare) * 100)}%` }}
                />
              </span>
              <span className="text-[0.625rem] tabular-nums text-gray-400">
                {s.shares[s.shares.length - 1].toFixed(1)}% of book
              </span>
            </div>
          </div>
        ))}
      </div>
    </Panel>
  );
};

/** "vs previous 13 weeks": the previous range's change, or why there is none. Never invented. */
const PreviousLine: React.FC<{ previous: PreviousRange; label: string; diff: number | null; format: (n: number) => string }> = ({
  previous, label, diff, format,
}) => (
  <span className="flex flex-wrap items-center gap-1.5" data-testid="vs-previous">
    {previous.status === 'ok' && diff !== null ? (
      <>
        <span>{`vs previous ${label}`}</span>
        <Chip tone={diff > 0 ? 'green' : diff < 0 ? 'red' : 'muted'}>{`${diff > 0 ? '+' : diff < 0 ? '-' : ''}${format(Math.abs(diff))}`}</Chip>
      </>
    ) : (
      <span className="text-gray-500">{previous.status === 'ok' ? 'No earlier change' : previous.reason}</span>
    )}
  </span>
);

const signed = (n: number, format: (v: number) => string): string =>
  `${n > 0 ? '+' : n < 0 ? '-' : ''}${format(Math.abs(n))}`;

const TrendsTab: React.FC<{ companyId: string }> = ({ companyId }) => {
  const { result, loading, error } = useTrendsReport(companyId);
  const [rangeKey, setRangeKey] = useState<RangeKey>(DEFAULT_RANGE);
  const [custom, setCustom] = useState<{ from: string; to: string } | undefined>(undefined);

  // Memoised, not a bare conditional. A fresh [] literal on every render is a
  // new identity, so the useMemo below would recompute the month bands on every
  // render and its dependency array would be decorative.
  const points: TrendPoint[] = useMemo(
    () => (result && result.status === 'ok' ? result.data.points : []),
    [result],
  );
  const reading = useMemo(
    () => (points.length === 0 ? null : readRange(points, resolveRange(rangeKey, points, rangeKey === 'custom' ? custom : undefined))),
    [points, rangeKey, custom],
  );

  if (loading) {
    return (
      <div className="space-y-6">
        <SkeletonStatCards count={4} columns={4} />
        <SkeletonTable rows={4} columns={5} />
      </div>
    );
  }

  if (error) {
    return <ToneBanner icon={AlertTriangle} color="#f59e0b">{error}</ToneBanner>;
  }

  if (!result || result.status === 'no_snapshot' || points.length === 0 || !reading) {
    return (
      <HousePanel title="History builds from your weekly uploads" icon={LineChart}>
        <p className="mx-auto max-w-[52ch] py-4 text-center text-[0.8125rem] text-gray-400">
          Upload a Performance export to start the series. Nothing here is modelled or back-filled,
          so the first number you see is a number you can act on.
        </p>
      </HousePanel>
    );
  }

  const latestOverall = points[points.length - 1];
  const { range, previous } = reading;
  // A custom window can hold no snapshot; the cards then read the newest one and say so.
  const ranged = reading.points.length > 0 ? reading.points : [latestOverall];
  const start = ranged[0];
  const latest = ranged[ranged.length - 1];
  const previousPoint = ranged.length > 1 ? ranged[ranged.length - 2] : undefined;
  const movement = previousPoint ? latest.deals - previousPoint.deals : null;
  const weekly = snapshotRows(ranged);
  const deals = change(start.deals, latest.deals);
  const value = change(start.loanAmountCents, latest.loanAmountCents);
  const hasSpan = ranged.length > 1;
  const prevDeals = previous.status === 'ok' ? start.deals - previous.prevStart.deals : null;
  const prevValue = previous.status === 'ok' ? start.loanAmountCents - previous.prevStart.loanAmountCents : null;
  const marks = milestones(points[0].asAtDate, points.length);
  const since = `since ${formatAsAt(start.asAtDate)}`;
  const pct = (p: number | null): string => (p === null ? '' : ` (${p > 0 ? '+' : ''}${p.toFixed(1)}%)`);

  return (
    <div className="space-y-6">
      <TrackerSection
        id="trends-book"
        step="1 · The book over time"
        title={`${num(points.length)} weekly snapshot${points.length === 1 ? '' : 's'}`}
        description={`${formatAsAt(points[0].asAtDate)} to ${formatAsAt(latestOverall.asAtDate)}.`}
      >
      {/* A range and its previous range of equal length (founder, 5 Oct 2026): the period-over-period
          read best practice asks for. A quarter (13W) is the default. */}
      <PeriodBar
        label="Range"
        ariaLabel="Trend range"
        presets={RANGE_PRESETS}
        selected={rangeKey}
        onSelect={(k) => setRangeKey(k as RangeKey)}
        onApplyCustom={(from, to) => { setCustom({ from, to }); setRangeKey('custom'); }}
        custom={custom}
        minDate={points[0].asAtDate}
        maxDate={latestOverall.asAtDate}
        applyLabel="Apply range"
        trailing={<span className="text-xs text-gray-400" data-testid="range-summary">
          {reading.points.length === 0
            ? 'No snapshot in this range'
            : `${formatAsAt(start.asAtDate)} to ${formatAsAt(latest.asAtDate)}, ${num(reading.points.length)} snapshot${reading.points.length === 1 ? '' : 's'}`}
        </span>}
      />

      <HouseStatGrid count={4} testId="trends-cards">
        <HouseStatCard
          label="Deals in flight"
          value={num(latest.deals)}
          sub={hasSpan ? `${arrow(deals.pct ?? deals.diff)} ${signed(deals.diff, num)}${pct(deals.pct)} ${since}` : `As at ${formatAsAt(latest.asAtDate)}`}
          info="Deals in flight at the range's last snapshot, against its first. The previous range is the same length immediately before, read at its own first snapshot."
          footer={<PreviousLine previous={previous} label={range.label} diff={prevDeals} format={num} />}
          icon={ChartLine}
          color={CHART_PALETTE[0]}
        />
        <HouseStatCard
          label="Pipeline value"
          value={money(latest.loanAmountCents)}
          sub={hasSpan ? `${arrow(value.pct ?? value.diff)} ${signed(value.diff, money)}${pct(value.pct)} ${since}` : formatCents(latest.loanAmountCents)}
          info="Loan amount of the deals in flight at the range's last snapshot, against its first."
          footer={<PreviousLine previous={previous} label={range.label} diff={prevValue} format={money} />}
          icon={DollarSign}
          color={CHART_PALETTE[2]}
          tone="good"
        />
        <HouseStatCard
          label="Week on week"
          value={movement === null ? '-' : `${movement > 0 ? '+' : ''}${movement}`}
          sub={previousPoint
            ? `from ${num(previousPoint.deals)} on ${formatAsAt(previousPoint.asAtDate)}`
            : 'needs a second snapshot'}
          info="The change in deals in flight between the last two snapshots in the range."
          footer={`Last upload ${formatAsAt(latest.asAtDate)}`}
          icon={ArrowUpDown}
          color={CHART_PALETTE[1]}
          tone={movement !== null && movement < 0 ? 'warn' : 'plain'}
        />
        <HouseStatCard
          label="Snapshots"
          value={num(reading.points.length)}
          sub={`${formatAsAt(start.asAtDate)} to ${formatAsAt(latest.asAtDate)}`}
          footer={`of ${num(points.length)} in the history`}
          icon={CalendarDays}
          color={CHART_PALETTE[3]}
        />
      </HouseStatGrid>

      {points.length < MOVEMENT_MIN_POINTS ? (
        <Panel
          title="Trends and conversion"
          icon={Milestone}
          accent={CHART_PALETTE[1]}
          badge={<Pill tone="warn">Builds from week one</Pill>}
        >
          <div className="py-6 text-center">
            <h3 className="text-[1.0625rem] font-semibold text-gray-100">
              {points.length === 1
                ? 'History builds from your weekly uploads'
                : `${points.length} snapshots captured`}
            </h3>
            <p className="mx-auto mt-2 max-w-[52ch] text-[0.8125rem] text-gray-400">
              Per-stage movement needs {MOVEMENT_MIN_POINTS} weekly uploads before a line through the
              points means anything; you have {points.length}. Nothing here is modelled or
              back-filled, so the first number you see is a number you can act on.
            </p>
            <div className="mx-auto mt-6 flex max-w-[44rem] flex-wrap gap-2">
              {marks.map((m) => (
                <div
                  key={m.when}
                  className={`flex-1 rounded-lg border p-2.5 text-center text-[0.6875rem] leading-snug ${
                    m.reached
                      ? 'border-blue-500/50 bg-blue-600/[0.14] font-semibold text-blue-300'
                      : 'border-dashed border-gray-700 text-gray-500'
                  }`}
                >
                  {m.when}
                  <br />
                  {m.what}
                </div>
              ))}
            </div>
          </div>
        </Panel>
      ) : reading.points.length < MOVEMENT_MIN_POINTS ? (
        <Panel title="Stages that moved" icon={Layers} accent={CHART_PALETTE[3]} badge={<Chip>{`${num(reading.points.length)} in this range`}</Chip>}>
          <p className="text-[0.8125rem] leading-relaxed text-gray-400">
            {`This range holds ${num(reading.points.length)} snapshot${reading.points.length === 1 ? '' : 's'}; per-stage movement needs ${MOVEMENT_MIN_POINTS} before a line through them means anything. Choose a longer range.`}
          </p>
        </Panel>
      ) : (
        <StageMovement reading={reading} />
      )}

      {/* The Dashboard's own chart, not a bespoke one.
          What it replaces never rendered: the bars lived in a `flex h-44 items-end`
          row, and align-items:flex-end stops the columns stretching, so each
          percentage height resolved against an indefinite height and collapsed to
          nothing. Every tenant saw counts floating over invisible bars, while the
          hatched "no history" blocks showed because their height was a fixed 6px.
          Reusing MonthlyBreakdownChart fixes it and brings the axes, gridlines,
          point labels and hover tooltip the rest of the product already has. */}
      {/* One month of history is a dot in an empty plot, not a trend. The bar
          chart this replaced had a "No history before X" affordance for exactly
          that case and the replacement dropped it; this restores an equivalent.
          The charts follow the range, so a 4W range inside one month reads the same way. */}
      {weekly.length < 2 ? (
        <Panel
          title="Book size and value"
          icon={ChartLine}
          accent={CHART_PALETTE[0]}
          badge={<Pill tone="warn">{`${num(weekly.length)} snapshot ${rangeKey === 'all' ? 'so far' : 'in this range'}`}</Pill>}
        >
          <p className="text-[0.8125rem] leading-relaxed text-gray-400">
            A line needs two snapshots to be a line. The current figures are in the cards above and
            every snapshot is listed below; this chart fills in from your second upload.
          </p>
        </Panel>
      ) : (
        <>
          <MonthlyBreakdownChart
            data={weekly}
            title="Deals in flight"
            dataKey="deals"
            color={CHART_PALETTE[0]}
            variant="count"
            unitLabel="deals"
            period={weekly.length > LABELLED_POINTS_MAX ? 'ALL' : undefined}
          />

          <MonthlyBreakdownChart
            data={weekly}
            title="Pipeline value"
            dataKey="value"
            color={CHART_PALETTE[2]}
            period={weekly.length > LABELLED_POINTS_MAX ? 'ALL' : undefined}
          />
        </>
      )}
      </TrackerSection>

      <TrackerSection
        id="trends-history"
        step="2 · History"
        title="Milestones and snapshots"
        description="Observed stage milestones between consecutive uploads, and every snapshot captured."
      >
      {/* Below the charts (founder, 29 Sep 2026): the charts are the picture, this is the
          interval-by-interval detail behind them, collapsed until asked for. */}
      <StageMovementPanel key={companyId} companyId={companyId} />

      <Panel title="Snapshots captured" icon={Camera} accent={CHART_PALETTE[5]} badge={<Chip tone="cyan">{num(points.length)}</Chip>}>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-gray-700/70 text-[0.6875rem] uppercase tracking-wider text-gray-400">
              <tr>
                <th className="px-3 py-2 font-semibold">As at</th>
                <th className="px-3 py-2 text-right font-semibold">In flight</th>
                <th className="px-3 py-2 text-right font-semibold">Value</th>
                <th className="px-3 py-2 text-right font-semibold">Outside the figures</th>
              </tr>
            </thead>
            <tbody>
              {/* Newest first (founder, 6 Oct 2026): the table is looked up, the charts are read
                  left to right, so only the charts run oldest first. */}
              {[...points].sort((x, y) => (x.asAtDate < y.asAtDate ? 1 : x.asAtDate > y.asAtDate ? -1 : 0)).map((p) => (
                <tr
                  key={p.asAtDate}
                  className={`border-b border-gray-800/80 text-gray-300 transition-colors odd:bg-white/[0.015] hover:bg-white/[0.04] ${
                    p.asAtDate >= range.from && p.asAtDate <= range.to ? '' : 'opacity-60'
                  }`}
                >
                  <td className="px-3 py-2 tabular-nums">{formatAsAt(p.asAtDate)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-100">{num(p.deals)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatCents(p.loanAmountCents)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-amber-400/80">
                    {p.unclassifiedDeals > 0 ? num(p.unclassifiedDeals) : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      {/* No "Stage mix at <date>" chips (founder, 5 Oct 2026): the Pipeline stage cards and funnel
          carry the same counts. */}
      <Honesty>
        <LineChart className="mr-1.5 inline h-3.5 w-3.5" />
        Stage-to-stage conversion is on the Pipeline tab. It follows each deal by its reference
        across these snapshots, so it sees a stage only when an upload catches a deal sitting in it,
        and it shows no rate until four snapshots exist. The more often you upload, the less it misses.
      </Honesty>
      </TrackerSection>
    </div>
  );
};

export default TrendsTab;
